import { Controller } from "@hotwired/stimulus"
import BaseGrid, { html, Handlebars } from "../utils/base_grid"

// Pre-compile templates
const actionButtonsTemplate = Handlebars.compile(`
  <div class="btn-group">
    <a href="{{viewUrl}}" class="btn btn-icon btn-sm" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{viewTitle}}">
      <i class="fas fa-eye"></i>
    </a>
    <a href="{{editUrl}}" class="btn btn-icon btn-sm" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{editTitle}}">
      <i class="fas fa-edit"></i>
    </a>
  </div>
`)

const nameLinkTemplate = Handlebars.compile(`
  <a href="{{viewUrl}}" class="text-decoration-none fw-bold" data-turbo-frame="_top">{{name}}</a>
`)

const typeBadgeTemplate = Handlebars.compile(`
  <span class="badge badge-type">{{type}}</span>
`)

const statusBadgeTemplate = Handlebars.compile(`
  <span class="badge badge-{{status}}">{{status}}</span>
`)

export default class extends Controller {
  static values = { 
    url: String,
    translations: Object  // { view: "View", edit: "Edit", name: "Name", type: "Type", etc. }
  }
  static targets = ["container", "skeleton", "searchInput", "typeFilter"]

  connect() {
    // Get translations with defaults
    const t = this.translationsValue || {}
    const labels = {
      name: t.name || "Name",
      type: t.type || "Type",
      status: t.status || "Status",
      updated: t.updated || "Updated",
      actions: t.actions || "Actions",
      view: t.view || "View",
      edit: t.edit || "Edit",
      ago: t.ago || "ago"
    }
    
    // Store type translations for later use
    this.typeTranslations = t.types || {}
    this.labels = labels

    this.baseGrid = new BaseGrid(this, {
      limit: 50,
      columns: [
        { 
          name: labels.name, 
          id: "name",
          formatter: (cell, row) => html(nameLinkTemplate({ name: cell, viewUrl: row.cells[5].data }))
        },
        { 
          name: labels.type, 
          id: "type",
          formatter: (cell) => {
            const translatedType = this.typeTranslations[cell] || cell
            return html(typeBadgeTemplate({ type: translatedType }))
          }
        },
        { 
          name: labels.status, 
          id: "status",
          formatter: (cell) => html(statusBadgeTemplate({ status: cell }))
        },
        { 
          name: labels.updated, 
          id: "updated_ago",
          formatter: (cell) => `${cell} ${labels.ago}`
        },
        { name: "ID", id: "id", hidden: true },
        { name: "ViewURL", id: "view_url", hidden: true },
        { name: "EditURL", id: "edit_url", hidden: true },
        { 
          name: labels.actions,
          sort: false,
          formatter: (cell, row) => {
            return html(actionButtonsTemplate({ 
              viewUrl: row.cells[5].data, 
              editUrl: row.cells[6].data,
              viewTitle: labels.view,
              editTitle: labels.edit
            })) 
          }
        }
      ],
      mapData: (item) => [
        item.name, 
        item.type, 
        item.status, 
        item.updated_ago,
        item.id, 
        item.view_url,
        item.edit_url
      ]
    })

    this.grid = this.baseGrid.create()

    // Listen for type filter changes
    this.typeFilterHandler = this.handleTypeFilter.bind(this)
    window.addEventListener("book-contents:type-filter", this.typeFilterHandler)
  }

  disconnect() {
    if (this.typeFilterHandler) {
      window.removeEventListener("book-contents:type-filter", this.typeFilterHandler)
    }
    this.baseGrid.destroy()
  }

  search(event) {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      const query = event.target.value.trim()
      this.baseGrid.updateWithFilters({ search: query })
    }, 300)
  }

  filterByType(event) {
    const type = event.target.value
    this.baseGrid.updateWithFilters({ type })
  }

  handleTypeFilter(event) {
    const { type } = event.detail
    this.baseGrid.updateWithFilters({ type })
  }
}
